import {
  CreateFormDocument,
  EngagementCreateSurveyDocument,
  FormsTypeEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import {
  NavigationResult,
  OneCreateAction,
  OneCreationActionResolver,
} from 'modules/core/lib/oneCreateActions/types'

import {
  defaultSections,
  FromQuestionaireToMutationVariables,
} from 'modules/forms/components/newFormEditor/helpers'
import { QuestionType, SectionType } from 'modules/forms/components/newFormEditor/types'
import { useUpdateNewForm } from 'modules/forms/hooks/useForms'

type AgentQuestion = QuestionType | SectionType

interface CreateSurveyArgs {
  title: string
  resourceType: string
  resourceId: string
  description: string
  formType: FormsTypeEnum
  anonymous?: boolean
  questions?: AgentQuestion[]
}

const useCreateSurvey: OneCreationActionResolver = () => {
  const { company, access } = useCurrentSession()
  const { navigation } = useNavigation()

  const [createEngagementSurvey, { loading: loadingEngagement }] = useMutation(
    EngagementCreateSurveyDocument,
    {
      extractorFn: (data) => data.surveysMutations.createSurveysSurvey,
      errorPolicy: 'all',
    }
  )

  const [createForm, { loading: loadingTraining }] = useMutation(CreateFormDocument, {
    extractorFn: (data) => data.formsMutations.createFormsForm,
    errorPolicy: 'all',
  })

  const { updateForm } = useUpdateNewForm()

  const createSurveyForTraining = async (
    args: CreateSurveyArgs
  ): Promise<NavigationResult | undefined> => {
    if (!company || !access) {
      return
    }

    const { title, description, resourceId, formType } = args

    const elements =
      formType === 'effectiveness' || formType === 'satisfaction' ? [defaultSections(formType)] : []

    const result = await createForm({
      variables: {
        title,
        description,
        resourceId,
        resourceType: 'Trainings::Training',
        anonymous: false,
        authorId: access.id,
        companyId: company.id,
        formType: formType || 'custom',
        elements,
      },
    })

    const handledResult = handleResult(result, (_errors) => {})

    if (handledResult.success && handledResult.data.form?.id) {
      const formId = handledResult.data.form.id

      return {
        to: navigation.trainings.list.details_revamp.trainingSurveyDetail.fullPath({
          id: String(resourceId),
          formId: String(formId),
        }),
        data: { survey: handledResult.data.form },
        followUp:
          'Tell the user you created this survey for them. Propose about 2-3 additional questions based on the training topic that they might want to add.',
      }
    }

    return undefined
  }

  const createDefaultSurvey = async (
    args: CreateSurveyArgs
  ): Promise<NavigationResult | undefined> => {
    if (!company || !access) {
      return
    }

    const { title, description, anonymous, questions } = args

    // First create the engagement survey to get the resourceId
    const surveyResult = await createEngagementSurvey({
      variables: {
        title,
        companyId: company.id,
      },
    })

    const surveyHandledResult = handleResult(surveyResult, (_errors) => {})

    if (!surveyHandledResult.success || !surveyHandledResult.data.survey?.id) {
      return undefined
    }

    const surveyId = surveyHandledResult.data.survey.id
    const formId = surveyHandledResult.data.survey.form.id

    await updateForm({
      ...FromQuestionaireToMutationVariables(formId, anonymous ?? false, {
        title,
        description,
        elements: questions ?? [],
      }),
    })

    return {
      to: navigation.engagement.smart_surveys.survey.fullPath({ surveyId }),
      data: { survey: surveyHandledResult.data.survey },
      followUp:
        'Tell the user you created this survey for them. Propose 2-3 additional questions based on the survey topic that they might want to add. Do not mention the survey ID.',
    }
  }

  const createSurveyHandler = async (
    args: CreateSurveyArgs
  ): Promise<NavigationResult | undefined> => {
    if (!company) {
      return
    }

    if (args.resourceType === 'trainings') {
      return createSurveyForTraining(args)
    }

    return createDefaultSurvey(args)
  }

  return {
    resolver: createSurveyHandler,
    loading: loadingEngagement || loadingTraining,
  }
}

export const CreateSurveyAction: OneCreateAction = {
  actionResolver: useCreateSurvey,
  policies: [],
}
